/**
 * Contrato FE — Avaliação NBR Enterprise - Automatic (MVP).
 * RQ-024A-FINAL · baixo risco · não liga algoritmos de regressão.
 *
 * Flag API: VALUATION_AUTOMATIC_V1 (default off — ver api/feature_flags_v1.py).
 * Não wirear em AppLayout sem item Executor / auth de cutover parcial.
 */

export const VALUATION_ENTERPRISE_FLAG = "VALUATION_AUTOMATIC_V1";

export const WIZARD_STEPS = Object.freeze([
  { id: 1, key: "solicitacao", label: "Solicitação e documentos" },
  { id: 2, key: "avaliando", label: "Imóvel avaliando" },
  { id: 3, key: "endereco_mapa", label: "Endereço e mapa" },
  { id: 4, key: "comparaveis", label: "Comparáveis e filtros" },
  { id: 5, key: "enriquecimento", label: "Enriquecimento" },
  { id: 6, key: "regressao", label: "Regressão" },
  { id: 7, key: "resultado", label: "Resultado" },
  { id: 8, key: "relatorios", label: "Relatórios / API" }
]);

/** Filtro de área privativa — P0 Item 3C */
export const AREA_FILTER_OPTIONS = Object.freeze([
  { id: "none", label: "Sem destaque", rule: "all" },
  // Até 200% da área do avaliando (ratio max 2.0) — contrato cliente; não "200% maior".
  { id: "m50_p200", label: "Área 50% … 200%", rule: { minRatio: 0.5, maxRatio: 2.0 } },
  { id: "pm25", label: "Área ±25%", rule: { minRatio: 0.75, maxRatio: 1.25 } },
  { id: "pm50", label: "Área ±50%", rule: { minRatio: 0.5, maxRatio: 1.5 } }
]);

/** Fator oferta — RQ-127: no clone, desconto no valor final (0/5/10). Default 0%. */
export const OFFER_FACTOR_OPTIONS = Object.freeze([
  { id: "offer_0", label: "0%", rate: 0 },
  { id: "offer_5", label: "5%", rate: 0.05 },
  { id: "offer_10", label: "10%", rate: 0.1 }
]);

/** @deprecated RQ-127 — elasticidade comercial removida do clone; mantido só para legado/oficial. */
export const ELASTICITY_DEFAULT_RATE = 0.05;

/** Política R² — alerta + confirmação (não hard-block) */
export const R2_OFFICIAL_THRESHOLD = 0.8;
export const R2_POLICY = Object.freeze({
  threshold: R2_OFFICIAL_THRESHOLD,
  mode: "alert_and_confirm",
  confirmField: "confirm_official_despite_r2"
});

export const RADIUS_STEP_METERS = 50;
export const RADIUS_SUGGESTIONS_M = Object.freeze([100, 150, 200, 250, 300, 350, 400, 450, 500]);

export const POI_VALORIZANTE_TYPES = Object.freeze([
  "metro",
  "monotrilhos",
  "shopping",
  "orla",
  "grandes_parques"
]);

/** Estado inicial do wizard (DTO local — persistência via API /drafts, não properties) */
export function createEmptyEnterpriseDraft() {
  return {
    step: 1,
    draftId: null,
    request: {
      demandCode: "",
      osNumber: "",
      externalId: "",
      targetCityModel: "",
      solicitationMode: "free"
    },
    subject: {
      avaliacaoModo: "mercado",
      importarMatricula: "sim",
      cep: "",
      uf: "",
      municipio: "",
      distrito: "",
      bairro: "",
      logradouro: "",
      numero: "",
      numeroCondominio: "",
      unidade: "",
      sql: "",
      contribuinte: "",
      complemento: "",
      nomeEmpreendimento: "",
      setor: "",
      quadra: "",
      bloco: "",
      lote: "",
      conjunto: "",
      nrUnidade: "",
      idTipologia: "",
      areaPrivativa: null,
      areaComum: null,
      areaTotal: null,
      tipulo: "apartamento",
      tipoImplantacao: "Condominio",
      padrao: "",
      quartos: null,
      suites: null,
      banheiros: null,
      vagas: null,
      latitude: null,
      longitude: null,
      latDms: "",
      lonDms: "",
      datum: "SIRGAS2000_WGS84",
      googleMapsUrl: "",
      formattedAddress: "",
      geocodeProvider: "",
      geocodeStatus: "",
      geocodeConfidence: null,
      geocodeWarnings: [],
      geocodeAt: null,
      locationType: null,
      placeId: null,
      partialMatch: null,
      coordSource: null,
      googlePrecision: null
    },
    documents: {
      matricula: null,
      iptu: null,
      pdf: null,
      other: []
    },
    geoContext: null,
    sample: {
      radiusM: 500,
      areaFilterId: "pm25",
      requireFullAddress: false
    },
    enrichment: {
      offerFactorId: "offer_0",
      applyElasticity: false,
      elasticityRate: 0,
      poiLabel: null,
      ibgeRendaSubject: null
    },
    regression: {
      r2: null,
      confirmOfficialDespiteR2: false,
      alerts: []
    },
    result: {
      vu: null,
      total: null,
      totalAfterElasticity: null
    },
    resultUnlocked: false,
    evaluationId: null,
    lastRegressionRunId: null,
    sampleRevisions: [],
    comparablesPhase: "discovery",
    preliminaryCalculatedValue: null,
    preliminaryAdoptedValue: null,
    preliminaryRevision: null,
    selectedComparableIds: [],
    spatialWorkspace: null
  };
}

export function areaBoundsForFilter(areaM2, areaFilterId) {
  const area = Number(areaM2);
  if (!Number.isFinite(area) || area <= 0) return null;
  const opt = AREA_FILTER_OPTIONS.find((o) => o.id === areaFilterId);
  if (!opt || opt.rule === "all") return { min: 0, max: Number.POSITIVE_INFINITY };
  return {
    min: area * opt.rule.minRatio,
    max: area * opt.rule.maxRatio
  };
}

export function priceAfterOfferFactor(price, offerFactorId) {
  const p = Number(price);
  if (!Number.isFinite(p) || p <= 0) return null;
  const opt = OFFER_FACTOR_OPTIONS.find((o) => o.id === offerFactorId) || OFFER_FACTOR_OPTIONS[0];
  return p * (1 - opt.rate);
}

export function valueAfterElasticity(value, { apply = true, rate = ELASTICITY_DEFAULT_RATE } = {}) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  if (!apply) return v;
  return v * (1 - rate);
}

export function needsR2OfficialConfirmation(r2) {
  const x = Number(r2);
  if (!Number.isFinite(x)) return true;
  return x <= R2_OFFICIAL_THRESHOLD;
}
